'use client';

import {createContext, useCallback, useContext, useEffect, useState} from 'react';
import type {User, UserPermissions} from './types';
import {buildPublicUser, getUser} from 'common/users';

type AuthContextProps = {
  isReady: boolean;
  user: User;
  setUser: (user: User) => void;
  hasPermission: (permissionName: keyof UserPermissions, contentId?: string) => boolean;
  isPremium: boolean;
  isCoder: boolean;
};

const AuthContext = createContext<AuthContextProps>({
  isReady: false,
  user: buildPublicUser(),
  setUser: () => {},
  hasPermission: () => false,
  isPremium: false,
  isCoder: false,
});

export function AuthProvider({
  serverUser,
  children,
}: {
  serverUser?: User;
  children: React.ReactNode;
}) {
  const [isReady, setIsReady] = useState(!!serverUser);
  const [user, setUserState] = useState<User>(serverUser ?? buildPublicUser());

  const setUser = (u: User) => setUserState(u);
  const isPremium = user.subscriptions.some((sub) => sub.product_name === 'IQ Premium');
  const isCoder = user.subscriptions.some((sub) => sub.product_name === 'IQ Coder');

  const hasPermission = useCallback(
    (permissionName: keyof UserPermissions, contentId = '') => {
      return (
        user.permissions[permissionName].access ||
        user.permissions[permissionName].consumed.includes(contentId)
      );
    },
    [user],
  );

  useEffect(() => {
    if (serverUser) {
      setIsReady(true);
      return;
    }
    getUser().then((u) => {
      setUser(u);
      setIsReady(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthContext.Provider value={{isReady, user, setUser, hasPermission, isPremium, isCoder}}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
